interface IsEqualObject {
  [key: string]: unknown;
}

const areSpecialValuesEqual = (a: object, b: object): boolean | undefined => {
  // Compare dates by timestamp
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // Compare RegExps by string
  if (a instanceof RegExp && b instanceof RegExp) {
    return a.toString() === b.toString();
  }

  // Not a special type
  return undefined;
};

// Compares arrays and plain objects by checking their keys and values
const compareObjects = (
  a: object,
  b: object,
  objA: unknown[],
  objB: unknown[],
  isEqual: (
    a: unknown,
    b: unknown,
    objA: unknown[],
    objB: unknown[],
  ) => boolean,
): boolean => {
  // One is an array and the other isn't, so they cannot be equal
  const aIsArray = Array.isArray(a);
  const bIsArray = Array.isArray(b);
  if (aIsArray !== bIsArray) return false;

  // This stops infinite loops when an object contains itself
  for (let i = 0; i < objA.length; i++) {
    if (objA[i] === a) return objB[i] === b;
  }

  // Different number of keys means they cannot be equal
  const keys1 = Object.keys(a);
  const keys2 = Object.keys(b);
  if (keys1.length !== keys2.length) return false;

  objA.push(a);
  objB.push(b);
  try {
    // Check every key from a exists in b with equal value
    for (const key of keys1) {
      if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
      if (
        !isEqual(
          (a as IsEqualObject)[key],
          (b as IsEqualObject)[key],
          objA,
          objB,
        )
      ) {
        return false;
      }
    }

    return true;
  } finally {
    objA.pop();
    objB.pop();
  }
};

const isEqual = (
  a: unknown,
  b: unknown,
  objA: unknown[] = [],
  objB: unknown[] = [],
): boolean => {
  if (a === b) return true;

  if (typeof a === 'number' && typeof b === 'number') {
    return Number.isNaN(a) && Number.isNaN(b);
  }

  if (
    a == null ||
    b == null ||
    typeof a !== 'object' ||
    typeof b !== 'object'
  ) {
    return false;
  }

  const specialValuesEqual = areSpecialValuesEqual(a, b);
  if (specialValuesEqual !== undefined) return specialValuesEqual;

  return compareObjects(a, b, objA, objB, isEqual);
};

export default isEqual;
